import type { TokenStore } from "./auth";

export interface TbHttpConfig {
  /** Empty string = relative URLs (same-origin or proxied). Otherwise absolute. */
  baseUrl?: string;
  tokenStore?: TokenStore;
  authHeader?: string;            // default 'X-Authorization' (TB convention)
  onAuthExpired?: () => void;
}

export interface TbHttp {
  get<T>(path: string, init?: RequestInit): Promise<T>;
  post<T>(path: string, body: unknown, init?: RequestInit): Promise<T>;
  readonly baseUrl: string;
}

export class TbHttpError extends Error {
  constructor(public readonly status: number, body: string) {
    super(`TB HTTP ${status}: ${body.slice(0, 200)}`);
    this.name = "TbHttpError";
  }
}

/**
 * Thin REST client with a 401 → refresh → replay loop. Mirrors the TB-EMS
 * `createTbHttp` helper.
 */
export function createTbHttp(config: TbHttpConfig = {}): TbHttp {
  const baseUrl = config.baseUrl ?? "";
  const authHeader = config.authHeader ?? "X-Authorization";
  const store = config.tokenStore;

  async function doFetch(path: string, init: RequestInit, tokenOverride?: string | null) {
    const headers = new Headers(init.headers);
    const token = tokenOverride ?? store?.getToken();
    if (token) headers.set(authHeader, token);
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    return fetch(`${baseUrl}${path}`, { ...init, headers });
  }

  async function request<T>(path: string, init: RequestInit): Promise<T> {
    let res = await doFetch(path, init);
    if (res.status === 401 && store?.refresh) {
      const t = await store.refresh();
      if (t) res = await doFetch(path, init, t);
    }
    if (res.status === 401) {
      config.onAuthExpired?.();
      throw new TbHttpError(401, await res.text().catch(() => ""));
    }
    if (!res.ok) throw new TbHttpError(res.status, await res.text().catch(() => ""));
    // TB endpoints that take a write (e.g. POST SHARED_SCOPE attribute,
    // POST alarm ack/clear) return 200 OK with an EMPTY body. Calling
    // `.json()` on that throws a SyntaxError — Safari/WebKit renders it
    // as `DOMException: The string did not match the expected pattern.`
    // which surfaces as a confusing "save failed" toast even though the
    // server-side write succeeded. Read the body as text first; treat
    // empty / whitespace-only bodies as void; parse JSON only when the
    // body is non-empty AND looks like JSON.
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    if (!text || !text.trim()) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      // Non-JSON 2xx body. For void-typed POSTs this is fine; for typed
      // GETs the caller will treat undefined as missing data. We log so
      // it's visible without blowing up the page.
      console.warn(`[tb-http] ${path}: non-JSON 2xx body (${text.length} bytes) — treating as void.`);
      return undefined as T;
    }
  }

  return {
    baseUrl,
    get: (path, init = {}) => request(path, { ...init, method: "GET" }),
    post: (path, body, init = {}) => {
      const headers = new Headers(init.headers);
      headers.set("Content-Type", "application/json");
      return request(path, { ...init, method: "POST", body: JSON.stringify(body), headers });
    },
  };
}
